import { Immutable } from "immer";
import React from "react";
import { ErrorBoundary } from "tsi-common-react/src/components/ErrorBoundary/index";

import type { StoreLocatorForm as Value } from "@reactivated";

import styles from "./index.module.scss";

// Client-only loader for the retail-locator app. The locator lives inside the
// reactivated document's React tree (rendered here) rather than mounted by a
// separate createRoot/dynamicPlaceComponent scanner. Its dependency tree is not
// SSR-safe (Google Maps + Redux-driven UI), so it is lazy-imported and gated to
// client-only paint (see below), keeping the chunk code-split.
const StoreLocatorAppInner = React.lazy(
    () => import("../../apps/retail-locator/StoreLocatorAppInner"),
);

type IProps = Immutable<{
    value: Value | null;
}>;

/**
 * Renders the store-locator mount point. The block is a value-less
 * StaticBlock: the serialized value is an empty object (or null) and is
 * ignored.
 *
 * Emits the same ``<section data-place-react="retail-locator">`` mount-point
 * markup as the legacy path, but now renders the locator app as a child
 * of that section instead of leaving it empty for the imperative
 * ``dynamicPlaceComponent`` scanner. The app is gated to client-only paint by
 * the ``isHydrated`` flag: both the server render and the first client render
 * produce the bare mount section (no mismatch), and the real locator mounts
 * after hydration via the lazy import. The ``ErrorBoundary`` wraps ``Suspense``
 * so it catches both a lazy chunk-load failure and any render error from the
 * locator, scoping the blast radius to this section.
 */
export const StoreLocatorForm = (_props: IProps): React.JSX.Element => {
    const [isHydrated, setIsHydrated] = React.useState(false);
    React.useEffect(() => setIsHydrated(true), []);
    return (
        <section className={styles.retailers} data-place-react="retail-locator">
            {isHydrated && (
                <ErrorBoundary>
                    <React.Suspense fallback={null}>
                        <StoreLocatorAppInner />
                    </React.Suspense>
                </ErrorBoundary>
            )}
        </section>
    );
};
